import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import AIChatbot from "@/components/chat/AIChatbot";

const LOGO_CHROME = "https://customer-assets.emergentagent.com/job_minimal-arquitectos/artifacts/9hxuccqr_image.png";

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    api.getProject(id)
      .then((res) => setProject(res.data))
      .catch(() => navigate("/"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-neutral-200 border-t-neutral-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (!project) return null;

  const allImages = [project.cover_image, ...(project.gallery || [])];

  return (
    <div className="bg-white min-h-screen" data-testid="project-detail-page">
      {/* Sticky nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#2C2C2C]/95 backdrop-blur-2xl">
        <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between h-16 md:h-[72px]">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-white/50 hover:text-white/90 transition-colors text-sm"
            data-testid="back-button"
          >
            <ArrowLeft size={16} strokeWidth={1.5} />
            <span className="hidden sm:inline">Volver</span>
          </button>
          <img src={LOGO_CHROME} alt="Merza Soluciones" className="h-8" />
          <button
            onClick={() => navigate("/#contacto")}
            className="text-[12px] tracking-wide px-4 py-2 rounded-full bg-white/10 text-white/70 border border-white/10 hover:bg-white/20 transition-all"
            data-testid="project-contact-btn"
          >
            Contactar
          </button>
        </div>
      </nav>

      {/* Hero cover */}
      <div className="relative h-[60vh] md:h-[75vh] overflow-hidden pt-16">
        <img
          src={project.cover_image}
          alt={project.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-12 lg:px-24 pb-12 md:pb-16 max-w-[1800px] mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-white/50 text-[11px] tracking-[0.25em] uppercase font-semibold mb-2"
          >
            {project.category}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="text-4xl md:text-6xl lg:text-7xl font-medium text-white tracking-tight"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {project.title}
          </motion.h1>
        </div>
      </div>

      {/* Description */}
      <div ref={ref} className="px-6 md:px-12 lg:px-24 py-16 md:py-24 max-w-[1800px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-20">
          <div className="md:col-span-2">
            <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-neutral-400">
              Descripción
            </p>
          </div>
          <div className="md:col-span-10">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              className="text-xl md:text-2xl font-light text-neutral-600 leading-relaxed max-w-3xl"
            >
              {project.description}
            </motion.p>
          </div>
        </div>
      </div>

      {/* Gallery — Renders / Design */}
      {allImages.length > 1 && (
        <div className="px-6 md:px-12 lg:px-24 pb-16 md:pb-24 max-w-[1800px] mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="h-px flex-1 bg-neutral-200" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-neutral-400">
              Diseño
            </p>
            <div className="h-px flex-1 bg-neutral-200" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {allImages.map((img, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className={`overflow-hidden rounded-2xl ${i === 0 && allImages.length > 2 ? "md:col-span-2" : ""}`}
              >
                <img
                  src={img}
                  alt={`${project.title} - ${i + 1}`}
                  className={`w-full object-cover hover:scale-[1.02] transition-transform duration-700 ${
                    i === 0 && allImages.length > 2 ? "h-72 md:h-[550px]" : "h-64 md:h-[400px]"
                  }`}
                />
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Real Photos */}
      {project.real_photos && project.real_photos.length > 0 && (
        <div className="px-6 md:px-12 lg:px-24 pb-16 md:pb-24 max-w-[1800px] mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="h-px flex-1 bg-neutral-200" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-neutral-400">
              Resultado Final
            </p>
            <div className="h-px flex-1 bg-neutral-200" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {project.real_photos.map((img, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="overflow-hidden rounded-xl group"
              >
                <img
                  src={img}
                  alt={`${project.title} Real ${i + 1}`}
                  className="w-full h-48 md:h-64 object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Videos */}
      {project.videos && project.videos.length > 0 && (
        <div className="px-6 md:px-12 lg:px-24 pb-16 md:pb-24 max-w-[1800px] mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="h-px flex-1 bg-neutral-200" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-neutral-400">
              Recorrido Virtual
            </p>
            <div className="h-px flex-1 bg-neutral-200" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {project.videos.map((vid, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="overflow-hidden rounded-2xl bg-black"
                data-testid={`project-video-${i}`}
              >
                <video
                  src={vid}
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full h-64 md:h-[400px] object-cover"
                />
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="px-6 md:px-12 lg:px-24 pb-20 md:pb-28 max-w-[1800px] mx-auto text-center">
        <p className="text-neutral-400 text-sm mb-4">¿Te interesa un proyecto similar?</p>
        <button
          onClick={() => navigate("/#contacto")}
          className="group inline-flex items-center gap-3 bg-black text-white px-10 py-4 rounded-full text-sm font-medium hover:bg-neutral-800 transition-all active:scale-[0.97]"
          data-testid="project-cta-bottom"
        >
          Contáctanos
          <ArrowRight size={16} strokeWidth={2} className="group-hover:translate-x-1 transition-transform" />
        </button>
      </div>

      <AIChatbot />
    </div>
  );
}
